import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './Evolution.css';

const Evolution: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const evolutionStages = [
    {
      era: "2018-2019",
      title: "The Beginning",
      description: "Started building on the web with simple HTML, CSS, and JavaScript projects — learning by creating games and utilities.",
      projects: [
        { name: "Gallon-Feet-Conversion", description: "Unit conversion calculator" },
        { name: "MySnake", description: "Classic Snake game with custom sprites" },
        { name: "Pong", description: "Retro Pong game implementation" },
        { name: "RocketShip", description: "Space-themed action game" },
        { name: "WordEscape", description: "Word puzzle game" }
      ],
      color: "#FF6B6B"
    },
    {
      era: "2020-2021", 
      title: "Modern Frameworks",
      description: "Discovered component-based development with Vue.js and began exploring modern frontend architecture.",
      projects: [
        { name: "My_Vue_Website", description: "First Vue.js portfolio with components" }
      ],
      color: "#4ECDC4"
    },
    {
      era: "2022-2023",
      title: "Full Stack Development",
      description: "Transitioned to React and TypeScript, building full-stack applications with real user bases.",
      projects: [
        { name: "Bible Scramble", description: "Word puzzle game with React" },
        { name: "Bible Tiles", description: "Tile-matching game" },
        { name: "Christian Outreach", description: "Community platform" }
      ],
      color: "#45B7D1"
    },
    {
      era: "2024-Present",
      title: "jagoalt Studio",
      description: "Professional web design and development — shipping SaaS platforms, web apps, and digital products for real users.",
        projects: [
        { name: "Strandx", description: "Peer-to-peer roadside assistance (getstrandx.com)" },
        { name: "OpenAth", description: "Gym finder for traveling athletes" },
        { name: "Petz4Days", description: "Pet community platform" },
        { name: "Help A Brotha", description: "Community support platform (helpabrotha.org)" }
      ],
      color: "#06b6d4"
    }
  ];

  return (
    <div className="evolution">
      <motion.div 
        className="evolution-hero"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <h1 className="evolution-title">Our Journey</h1>
          <p className="evolution-subtitle">
            From first experiments to professional products — how jagoalt has grown over the years
          </p>
        </div>
      </motion.div>

      <div className="evolution-timeline">
        <motion.div 
          ref={ref}
          className="container"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {evolutionStages.map((stage, index) => (
            <motion.div 
              key={index} 
              className="timeline-item"
              variants={itemVariants}
              whileHover={{ x: 10, scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div className="timeline-marker" style={{ backgroundColor: stage.color }}>
                <span className="timeline-year">{stage.era}</span>
              </div>
              
              <div className="timeline-content">
                <div className="stage-header">
                  <h2 className="stage-title">{stage.title}</h2>
                  <div className="stage-color-bar" style={{ backgroundColor: stage.color }}></div>
                </div>
                
                <p className="stage-description">{stage.description}</p>
                
                <div className="stage-projects">
                  <h3>Key Projects</h3>
                  <div className="projects-grid">
                    {stage.projects.map((project, projectIndex) => (
                      <div key={projectIndex} className="project-card">
                        <h4>{project.name}</h4>
                        <p>{project.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div 
        className="evolution-footer"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <h2>Ready for what's next</h2>
          <p>
            Every project has been a stepping stone toward building better digital products. 
            The journey continues with each new client and challenge.
          </p>
          <motion.a 
            href="/projects" 
            className="cta-button"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            View Our Work
          </motion.a>
        </div>
      </motion.div>
    </div>
  );
};

export default Evolution;
